import {useEffect, useSyncExternalStore} from "react";
import {
	getDsContext,
	isDsContextReady,
	onDsContextChange,
} from "@/utils/dsContext";
import {
	ScoutingIndicesAPI,
	ProviderMatchesAPI,
} from "@/api/scouting_statistics";
const empty = () => ({status: "idle", data: null, error: null, busy: false});
const listeners = new Set();
const subscribe = (listener) => {
	listeners.add(listener);
	return () => listeners.delete(listener);
};
const emit = () => listeners.forEach((fn) => fn());
let config = {state: empty(), pending: null, revision: 0};
const providers = new Map();
onDsContextChange(() => {
	config = {state: empty(), pending: null, revision: 0};
	providers.clear();
	emit();
});
const provider = (id) => {
	if (!providers.has(id))
		providers.set(id, {state: empty(), pending: null, revision: 0});
	return providers.get(id);
};
const patch = (entry, changes) => {
	entry.state = {...entry.state, ...changes};
	emit();
};
export const indicesData = () => {
	getDsContext();
	return config.state;
};
export const providerData = (id) => {
	getDsContext();
	return provider(id).state;
};
const read = (entry, load, force = false) => {
	const context = getDsContext();
	if (!isDsContextReady())
		return Promise.reject(Error("Sessione non disponibile."));
	if (entry.pending && !force) return entry.pending;
	const ticket = ++entry.revision;
	patch(entry, {
		status:
			entry.state.status === "ready" && entry.state.data ? "ready" : "loading",
		refreshing: true,
		error: null,
	});
	const request = load()
		.then((data) => {
			if (context !== getDsContext() || entry.revision !== ticket)
				throw Error("Risposta superata.");
			patch(entry, {status: "ready", data, error: null, refreshing: false});
			return data;
		})
		.catch((error) => {
			if (context === getDsContext() && entry.revision === ticket)
				patch(entry, {status: "error", data: null, error, refreshing: false});
			throw error;
		})
		.finally(() => {
			if (entry.pending === request) entry.pending = null;
		});
	entry.pending = request;
	return request;
};
export const refreshIndices = (force = false) => {
	getDsContext();
	return read(config, ScoutingIndicesAPI.list, force);
};
export const refreshProvider = (id, force = false) => {
	getDsContext();
	return read(provider(id), () => ProviderMatchesAPI.get(id), force);
};
const mutate = async (entry, operation, reload) => {
	const context = getDsContext();
	if (
		!isDsContextReady() ||
		entry.state.status !== "ready" ||
		!entry.state.data?.canWrite ||
		entry.state.busy
	)
		throw Error("Modifica non disponibile.");
	++entry.revision;
	entry.pending = null;
	patch(entry, {busy: true});
	try {
		const result = await operation();
		if (context !== getDsContext()) throw Error("Sessione cambiata.");
		try {
			await reload(result);
		} catch {
			/* Confirmed write remains successful; the read error stays visible. */
		}
		return result;
	} finally {
		if (context === getDsContext()) patch(entry, {busy: false});
	}
};
export const saveScoutingIndex = (index) => {
	getDsContext();
	return mutate(
		config,
		() => ScoutingIndicesAPI.save(index),
		() => refreshIndices(true),
	);
};
export const removeScoutingIndex = (index) => {
	getDsContext();
	return mutate(
		config,
		() => ScoutingIndicesAPI.remove(index),
		() => refreshIndices(true),
	);
};
export const initializeScoutingIndices = () => {
	getDsContext();
	return mutate(config, ScoutingIndicesAPI.defaults, () =>
		refreshIndices(true),
	);
};
export const importProvider = (id, file, fields) => {
	getDsContext();
	const entry = provider(id);
	return mutate(
		entry,
		() => ProviderMatchesAPI.import(id, file, fields),
		async (data) => patch(entry, {status: "ready", data, error: null}),
	);
};
function useData(snapshot, refresh) {
	const context = getDsContext();
	const current = useSyncExternalStore(subscribe, snapshot, snapshot);
	useEffect(() => {
		if (isDsContextReady()) refresh().catch(() => {});
		const reload = () => {
			if (!document.hidden && !snapshot().busy && isDsContextReady())
				refresh().catch(() => {});
		};
		window.addEventListener("focus", reload);
		const timer = setInterval(reload, 30000);
		return () => {
			window.removeEventListener("focus", reload);
			clearInterval(timer);
		};
	}, [context, refresh, snapshot]);
	return {
		...current,
		canWrite:
			current.status === "ready" && !!current.data?.canWrite && !current.busy,
		refresh,
	};
}
const configSnapshot = () => indicesData();
const configRefresh = (force = false) => refreshIndices(force);
export const useScoutingIndices = () => useData(configSnapshot, configRefresh);
const readers = new Map();
export const useProviderMatches = (id) => {
	if (!readers.has(id))
		readers.set(id, {
			snapshot: () => providerData(id),
			refresh: (force = false) => refreshProvider(id, force),
		});
	const pair = readers.get(id);
	return useData(pair.snapshot, pair.refresh);
};
